/* eslint-disable react/require-default-props */
/* eslint-disable no-param-reassign */
/* eslint-disable react/destructuring-assignment */
import {
  ActionIcon,
  Badge,
  Button,
  Grid,
  Group,
  Modal,
  Paper,
  Space,
  Text,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import { IconCheck, IconDeviceFloppy, IconNotes, IconPlus } from '@tabler/icons-react';
import { useEffect, useMemo, useRef } from 'react';
import BasicPricing, { BasicPricingData, BasicPricingRef } from '../../../../components/BasicPricing';
import BasicValueUnits, {
  BasicValueUnitsData,
  BasicValueUnitsRef,
} from '../../../../components/BasicValueUnits';
import EquipmentPicker from '../../../../components/core/EquipmentPicker/EquipmentPicker';
import PageSection from '../../../../components/core/PageSection/PageSection';
import { UnitOfMeasureType } from '../../../../models/core/cache.type';
import { Action } from '../../../../models/core/core.type';
import { EquipmentType } from '../../../../models/core/equipment.type';
import {
  ProposalResiduePlanEquipmentType,
  ProposalResiduePlanQuotationEquipmentType,
  ProposalResiduePlanQuotationType,
  ProposalResiduePlanType,
  ProposalTipoPrecificacaoEnum,
} from '../../../../models/core/proposals.type';
import { Feature, Financial } from '../../../../utils/constants.utils';
import { buildFakeAuditObject, newGuid } from '../../../../utils/helper.utils';
import DeleteConfirmPopover from '../../../../components/DeleteConfirmPopover';
import ResidueQuotationEquipmentForm, {
  ResidueQuotationEquipmentFormRef,
} from './ResidueQuotationEquipmentForm';

type FormViewData = {
  action: Action;
  id: string;
  idResiduoEquipamento: number | null;
  residuoEquipamento: string;
  residuoEquipamentoDescricao: string | null;
  quantidade: number | null;
  quantidadeIdUnidadeMedida: string | null;
  precificacao: BasicPricingData;
  observacao: string | null;
};

type ResiduePlanEquipmentModalProps = {
  opened: boolean;
  onClose: () => void;
  modoEdicao: boolean;
  requireSupplier?: boolean;
  referenceData: {
    unitOfMeasures: UnitOfMeasureType[];
  };
  plan?: ProposalResiduePlanType | null;
  origItem: ProposalResiduePlanEquipmentType | null;
  item: ProposalResiduePlanEquipmentType | null;
  idPropostaResiduoPlano?: number;
  zIndex: number;
  callback?: (
    item: ProposalResiduePlanEquipmentType | null,
    action: string,
    confirmed: boolean
  ) =>
    | ProposalResiduePlanEquipmentType
    | null
    | void
    | Promise<ProposalResiduePlanEquipmentType | null | void>;
  onAddQuotation?: (plan: ProposalResiduePlanType) => void;
  onSetPrimaryQuotation?: (
    plan: ProposalResiduePlanType,
    quotation: ProposalResiduePlanQuotationType
  ) => void;
  onDeleteQuotation?: (plan: ProposalResiduePlanType, quotation: ProposalResiduePlanQuotationType) => void;
  onQuotationSubmit?: (
    quotation: ProposalResiduePlanQuotationType,
    refItem: ProposalResiduePlanEquipmentType,
    item: ProposalResiduePlanQuotationEquipmentType | null,
    action: string
  ) => void;
};

const buildInitialValues = (
  item: ProposalResiduePlanEquipmentType | null,
  origItem: ProposalResiduePlanEquipmentType | null,
  unitOfMeasures: UnitOfMeasureType[]
): FormViewData => ({
  action: origItem ? Action.Nothing : Action.Add,
  id: item?.id || item?.idTemporaria || item?.idPropostaResiduoPlanoEquipamento?.toString() || newGuid(),
  idResiduoEquipamento: item?.idResiduoEquipamento || null,
  residuoEquipamento: item?.residuoEquipamento || '',
  residuoEquipamentoDescricao: item?.residuoEquipamentoDescricao || null,
  quantidade: item?.quantidade || 1,
  quantidadeIdUnidadeMedida:
    item?.quantidadeIdUnidadeMedida?.toString() ||
    unitOfMeasures
      .find((x) => x.tipo === 'Outro' && x.unidadeMedidaSigla === 'un')
      ?.idUnidadeMedida.toString() ||
    null,
  precificacao: {
    tipoPrecificacao: item?.precificacao?.tipo || ProposalTipoPrecificacaoEnum.Geral,
    quantidade: item?.precificacao?.quantidade ?? null,
    quantidadeIdUnidadeMedida:
      item?.precificacao?.quantidadeIdUnidadeMedida?.toString() ||
      unitOfMeasures
        .find((x) => x.tipo === 'Outro' && x.unidadeMedidaSigla === 'un')
        ?.idUnidadeMedida.toString() ||
      null,
    frequenciaIdUnidadeMedida: item?.precificacao?.frequenciaIdUnidadeMedida?.toString() || null,
    preco: item?.precificacao?.preco ?? null,
    imposto: item?.precificacao?.imposto ?? Financial.Tax,
  },
  observacao: item?.observacao || '',
});

export default function ResiduePlanEquipmentModal(props: ResiduePlanEquipmentModalProps) {
  const refQuantity = useRef<BasicValueUnitsRef>(null);
  const refPricing = useRef<BasicPricingRef>(null);
  const quotationFormRefs = useRef<Record<string, ResidueQuotationEquipmentFormRef | null>>({});

  const form = useForm<FormViewData>({
    initialValues: buildInitialValues(props.item, props.origItem, props.referenceData.unitOfMeasures),
    validate: {
      idResiduoEquipamento: (value) => (!value || value <= 0 ? 'Campo obrigatório' : null),
    },
  });

  useEffect(() => {
    form.setValues(buildInitialValues(props.item, props.origItem, props.referenceData.unitOfMeasures));
    form.resetDirty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.item, props.origItem, props.opened]);

  let modalActionLabel = 'Adicionar';
  if (props.item) {
    modalActionLabel = props.modoEdicao ? 'Editar' : 'Visualizar';
  }

  const quantityUnitOptions = props.referenceData.unitOfMeasures
    .filter((x) => x.tipo === 'Outro' && x.unidadeMedidaSigla === 'un')
    .map((x) => ({
      value: x.idUnidadeMedida.toString(),
      label: `${x.unidadeMedida} (${x.unidadeMedidaSigla})`,
      group: x.tipo,
    }));

  const frequencyUnitOptions = props.referenceData.unitOfMeasures
    .filter(
      (x) =>
        (x.tipo === 'Tempo' &&
          (x.unidadeMedidaSigla === 'd' ||
            x.unidadeMedidaSigla === 'sem' ||
            x.unidadeMedidaSigla === 'mês' ||
            x.unidadeMedidaSigla === 'ano')) ||
        (x.tipo === 'Outro' && x.unidadeMedidaSigla === 'per')
    )
    .map((x) => ({
      value: x.idUnidadeMedida.toString(),
      label: `${x.unidadeMedida} (${x.unidadeMedidaSigla})`,
      group: x.tipo,
    }));

  const buildDraftRefItem = useMemo<ProposalResiduePlanEquipmentType>(() => {
    const quantidadeIdUnidadeMedida = form.values.quantidadeIdUnidadeMedida
      ? Number(form.values.quantidadeIdUnidadeMedida)
      : null;
    const precificacaoQuantidadeIdUnidadeMedida = form.values.precificacao.quantidadeIdUnidadeMedida
      ? Number(form.values.precificacao.quantidadeIdUnidadeMedida)
      : null;
    const precificacaoFrequenciaIdUnidadeMedida = form.values.precificacao.frequenciaIdUnidadeMedida
      ? Number(form.values.precificacao.frequenciaIdUnidadeMedida)
      : null;

    return {
      ...buildFakeAuditObject(),
      ...(props.origItem || {}),
      ...form.values,
      id: form.values.id,
      idTemporaria: props.origItem?.idTemporaria || form.values.id,
      idPropostaResiduoPlanoEquipamento:
        props.origItem?.idPropostaResiduoPlanoEquipamento || props.item?.idPropostaResiduoPlanoEquipamento,
      idPropostaResiduoPlano: props.idPropostaResiduoPlano || props.origItem?.idPropostaResiduoPlano || -1,
      quantidade: Number(form.values.quantidade || 0),
      quantidadeIdUnidadeMedida: quantidadeIdUnidadeMedida || 0,
      quantidadeUnidadeMedida:
        props.referenceData.unitOfMeasures.find((x) => x.idUnidadeMedida === quantidadeIdUnidadeMedida)
          ?.unidadeMedida || '',
      quantidadeUnidadeMedidaSigla:
        props.referenceData.unitOfMeasures.find((x) => x.idUnidadeMedida === quantidadeIdUnidadeMedida)
          ?.unidadeMedidaSigla || null,
      precificacao: {
        tipo: form.values.precificacao.tipoPrecificacao,
        quantidade: form.values.precificacao.quantidade,
        quantidadeIdUnidadeMedida: precificacaoQuantidadeIdUnidadeMedida,
        quantidadeUnidadeMedida:
          props.referenceData.unitOfMeasures.find(
            (x) => x.idUnidadeMedida === precificacaoQuantidadeIdUnidadeMedida
          )?.unidadeMedida || null,
        quantidadeUnidadeMedidaSigla:
          props.referenceData.unitOfMeasures.find(
            (x) => x.idUnidadeMedida === precificacaoQuantidadeIdUnidadeMedida
          )?.unidadeMedidaSigla || null,
        frequenciaIdUnidadeMedida: precificacaoFrequenciaIdUnidadeMedida,
        frequenciaUnidadeMedida:
          props.referenceData.unitOfMeasures.find(
            (x) => x.idUnidadeMedida === precificacaoFrequenciaIdUnidadeMedida
          )?.unidadeMedida || null,
        frequenciaUnidadeMedidaSigla:
          props.referenceData.unitOfMeasures.find(
            (x) => x.idUnidadeMedida === precificacaoFrequenciaIdUnidadeMedida
          )?.unidadeMedidaSigla || null,
        preco: form.values.precificacao.preco,
        imposto: form.values.precificacao.imposto,
      },
    } as ProposalResiduePlanEquipmentType;
  }, [
    form.values,
    props.idPropostaResiduoPlano,
    props.item,
    props.origItem,
    props.referenceData.unitOfMeasures,
  ]);

  const visibleQuotations = (props.plan?.cotacoes || []).filter(
    (quotation) => (quotation.action || Action.Nothing) !== Action.Delete
  );

  const canAddQuotation = useMemo(() => {
    const hasEquipment = !!form.values.idResiduoEquipamento;
    const hasPlanQuantity =
      form.values.quantidade !== null &&
      !Number.isNaN(Number(form.values.quantidade)) &&
      Number(form.values.quantidade) > 0 &&
      !!form.values.quantidadeIdUnidadeMedida;
    const hasRequiredPricing =
      form.values.precificacao.tipoPrecificacao !== ProposalTipoPrecificacaoEnum.Geral ||
      (form.values.precificacao.quantidade !== null &&
        !Number.isNaN(Number(form.values.precificacao.quantidade)) &&
        !!form.values.precificacao.quantidadeIdUnidadeMedida &&
        !!form.values.precificacao.frequenciaIdUnidadeMedida &&
        form.values.precificacao.preco !== null &&
        !Number.isNaN(Number(form.values.precificacao.preco)) &&
        form.values.precificacao.imposto !== null &&
        !Number.isNaN(Number(form.values.precificacao.imposto)));

    return hasEquipment && hasPlanQuantity && hasRequiredPricing;
  }, [form.values]);

  const matchesQuotationItem = (
    quotationItem: ProposalResiduePlanQuotationEquipmentType,
    refItem: ProposalResiduePlanEquipmentType
  ) => {
    const refKeys = [refItem.id, refItem.idTemporaria, refItem.idPropostaResiduoPlanoEquipamento].filter(
      (value) => value !== null && value !== undefined && value !== ''
    );

    const quotationKeys = [
      quotationItem.id,
      quotationItem.idTemporaria,
      quotationItem.idPropostaResiduoPlanoEquipamento,
    ].filter((value) => value !== null && value !== undefined && value !== '');

    if (refKeys.length && quotationKeys.length) {
      return refKeys.some((refKey) =>
        quotationKeys.some((quotationKey) => `${refKey}` === `${quotationKey}`)
      );
    }

    return quotationItem.idResiduoEquipamento === refItem.idResiduoEquipamento;
  };

  const getQuotationKey = (quotation: ProposalResiduePlanQuotationType) =>
    quotation.id || quotation.idPropostaResiduoPlanoCotacao?.toString() || quotation.idTemporaria || '';

  const visibleEquipmentQuotations = visibleQuotations;

  const synchronizeGeneralPricingToQuotations = (
    refItem: ProposalResiduePlanEquipmentType | null | undefined
  ) => {
    if (
      !refItem ||
      refItem.precificacao?.tipo !== ProposalTipoPrecificacaoEnum.Geral ||
      !props.plan ||
      !props.onQuotationSubmit
    ) {
      return;
    }

    visibleEquipmentQuotations.forEach((quotation) => {
      const quotationItem =
        quotation.equipamentos.find(
          (entry) =>
            (entry.action || Action.Nothing) !== Action.Delete && matchesQuotationItem(entry, refItem)
        ) || null;

      if (!quotationItem) {
        return;
      }

      props.onQuotationSubmit?.(
        quotation,
        refItem,
        {
          ...quotationItem,
          action: quotationItem.idPropostaResiduoPlanoCotacaoEquipamento
            ? Action.Modify
            : quotationItem.action || Action.Nothing,
          precificacao: { ...refItem.precificacao },
        },
        quotationItem.idPropostaResiduoPlanoCotacaoEquipamento ? Action.Modify : Action.Nothing
      );
    });
  };

  useEffect(() => {
    if (!props.opened) {
      quotationFormRefs.current = {};
    }
  }, [props.opened]);

  const isModified = (
    quantityData?: BasicValueUnitsData | null,
    pricingData?: BasicPricingData | null
  ): boolean => {
    const origData = `${props.origItem?.idResiduoEquipamento || ''}
    |${Number(props.origItem?.quantidade) || ''}
    |${props.origItem?.quantidadeIdUnidadeMedida || ''}
    |${props.origItem?.precificacao?.tipo || ''}
    |${Number(props.origItem?.precificacao?.quantidade) || ''}
    |${props.origItem?.precificacao?.quantidadeIdUnidadeMedida || ''}
    |${props.origItem?.precificacao?.frequenciaIdUnidadeMedida || ''}
    |${Number(props.origItem?.precificacao?.preco) || ''}
    |${Number(props.origItem?.precificacao?.imposto) || ''}
    |${props.origItem?.observacao || ''}`;

    const formData = `${form.values.idResiduoEquipamento || ''}
    |${Number(quantityData?.valor) || ''}|${quantityData?.idUnidade1 || ''}
    |${pricingData?.tipoPrecificacao || ''}
    |${Number(pricingData?.quantidade) || ''}
    |${pricingData?.quantidadeIdUnidadeMedida || ''}
    |${pricingData?.frequenciaIdUnidadeMedida || ''}
    |${Number(pricingData?.preco) || ''}
    |${Number(pricingData?.imposto) || ''}
    |${form.values.observacao || ''}`;

    return origData !== formData;
  };

  const savePlanItem = async (
    action = 'callback',
    closeOnSuccess = false
  ): Promise<ProposalResiduePlanEquipmentType | null> => {
    if (!props.modoEdicao) {
      if (closeOnSuccess) {
        props.onClose();
      }
      return null;
    }

    const quantityData = refQuantity.current?.validate();
    const pricingData = refPricing.current?.validate();

    if (form.validate().hasErrors || !quantityData || !pricingData) {
      return null;
    }

    let values = { ...form.values };
    if (props.origItem) {
      if (isModified(quantityData, pricingData) && props.origItem.idPropostaResiduoPlanoEquipamento) {
        values.action = Action.Modify;
      } else {
        values = {
          ...props.origItem,
          ...values,
          action: props.item?.action || Action.Nothing,
          quantidadeIdUnidadeMedida: quantityData?.idUnidade1 || '',
        };
      }
    }

    const formItem: ProposalResiduePlanEquipmentType = {
      ...buildFakeAuditObject(),
      ...(props.origItem || {}),
      ...values,
      action: values.action,
      id: values.id,
      idTemporaria: props.origItem?.idTemporaria || values.id,
      idPropostaResiduoPlano: props.idPropostaResiduoPlano || props.origItem?.idPropostaResiduoPlano || -1,
      quantidade: Number(quantityData.valor),
      quantidadeIdUnidadeMedida: quantityData.idUnidade1 || '',
      precificacao: {
        tipo: pricingData.tipoPrecificacao,
        quantidade: pricingData.quantidade,
        quantidadeIdUnidadeMedida: pricingData.quantidadeIdUnidadeMedida
          ? Number(pricingData.quantidadeIdUnidadeMedida)
          : null,
        quantidadeUnidadeMedida: null,
        quantidadeUnidadeMedidaSigla: null,
        frequenciaIdUnidadeMedida: pricingData.frequenciaIdUnidadeMedida
          ? Number(pricingData.frequenciaIdUnidadeMedida)
          : null,
        frequenciaUnidadeMedida: null,
        frequenciaUnidadeMedidaSigla: null,
        preco: pricingData.preco,
        imposto: pricingData.imposto,
      },
    };

    formItem.quantidadeIdUnidadeMedida = Number(formItem.quantidadeIdUnidadeMedida);
    formItem.quantidadeUnidadeMedida =
      props.referenceData.unitOfMeasures.find((x) => x.idUnidadeMedida === Number(quantityData.idUnidade1))
        ?.unidadeMedida || '';
    formItem.precificacao.quantidadeUnidadeMedida =
      props.referenceData.unitOfMeasures.find(
        (x) => x.idUnidadeMedida === formItem.precificacao.quantidadeIdUnidadeMedida
      )?.unidadeMedida || null;
    formItem.precificacao.quantidadeUnidadeMedidaSigla =
      props.referenceData.unitOfMeasures.find(
        (x) => x.idUnidadeMedida === formItem.precificacao.quantidadeIdUnidadeMedida
      )?.unidadeMedidaSigla || null;
    formItem.precificacao.frequenciaUnidadeMedida =
      props.referenceData.unitOfMeasures.find(
        (x) => x.idUnidadeMedida === formItem.precificacao.frequenciaIdUnidadeMedida
      )?.unidadeMedida || null;
    formItem.precificacao.frequenciaUnidadeMedidaSigla =
      props.referenceData.unitOfMeasures.find(
        (x) => x.idUnidadeMedida === formItem.precificacao.frequenciaIdUnidadeMedida
      )?.unidadeMedidaSigla || null;
    formItem.observacao = formItem.observacao?.trim() || null;

    const callbackResult = await props.callback?.(formItem, action, true);
    const savedItem = callbackResult || formItem;

    synchronizeGeneralPricingToQuotations(savedItem);

    if (closeOnSuccess) {
      props.onClose();
    }

    return savedItem;
  };

  const handleSubmit = async (action = 'callback') => {
    if (!props.modoEdicao) {
      props.onClose();
      return;
    }

    const savedPlanItem = await savePlanItem(action, false);

    if (!savedPlanItem) {
      return;
    }

    const submitResults = await visibleEquipmentQuotations.reduce<Promise<(boolean | undefined)[]>>(
      async (promiseAcc, quotation) => {
        const acc = await promiseAcc;
        const result = await quotationFormRefs.current[getQuotationKey(quotation)]?.submit('autosave');
        acc.push(result);
        return acc;
      },
      Promise.resolve([])
    );

    if (submitResults.some((result) => result === false)) {
      return;
    }

    props.onClose();
  };

  return (
    <Modal
      opened={props.opened}
      onClose={props.onClose}
      title={`Plano/Equipamento - ${modalActionLabel}`}
      size="75%"
      zIndex={props.zIndex}
      closeOnClickOutside={false}
      closeOnEscape={false}
    >
      <Paper shadow="xs" p="md" withBorder>
        <PageSection
          size="lg"
          color={Feature.Home.Proposal.color}
          label="Plano de Equipamento"
          text="Defina o equipamento, a quantidade operacional e as observações deste item."
        />
        <Space h="xs" />
        <Grid columns={6}>
          <Grid.Col span={3}>
            <EquipmentPicker
              label="Equipamento"
              formItem={form.getInputProps('idResiduoEquipamento')}
              idsToBeDisabled={[]}
              callback={(item: EquipmentType | null) => {
                if (item) {
                  form.setFieldValue('idResiduoEquipamento', item.idResiduoEquipamento);
                  form.setFieldValue('residuoEquipamento', item.residuoEquipamento);
                  form.setFieldValue('residuoEquipamentoDescricao', item.descricao);
                }
              }}
              disabled={!props.modoEdicao}
              zIndex={props.zIndex + 1}
              useCache
            />
          </Grid.Col>
          <Grid.Col span={3}>
            <BasicValueUnits
              ref={refQuantity}
              isEdit={props.modoEdicao}
              disabled={!props.modoEdicao}
              disableUnit1
              zIndex={props.zIndex + 1}
              label="Quantidade"
              showUnit2={false}
              validationMode="all"
              valueMin={1}
              valueDefaultValue={1}
              valueStep={1}
              value={{
                valor: form.values.quantidade,
                idUnidade1: form.values.quantidadeIdUnidadeMedida,
                idUnidade2: null,
              }}
              unit1Options={quantityUnitOptions}
              onChange={(quantityData) => {
                form.setFieldValue('quantidade', quantityData.valor);
                form.setFieldValue('quantidadeIdUnidadeMedida', quantityData.idUnidade1);
              }}
            />
          </Grid.Col>
        </Grid>
        <Space h="xs" />
        <Textarea
          leftSection={<IconNotes size={15} />}
          label="Observação"
          placeholder="Observação sobre o equipamento"
          autosize
          maxLength={500}
          disabled={!props.modoEdicao}
          {...form.getInputProps('observacao')}
        />
      </Paper>
      <Space h="xs" />
      <Paper shadow="xs" p="md" withBorder>
        <PageSection
          size="lg"
          color={Feature.Home.Proposal.color}
          label="Precificação do Cliente"
          text="Representa os valores que serão cobrados do cliente para este item do plano."
        />
        <Space h="xs" />
        <BasicPricing
          ref={refPricing}
          isEdit={props.modoEdicao}
          disabled={!props.modoEdicao}
          zIndex={props.zIndex + 1}
          quantityMin={0}
          quantityDefaultValue={0}
          quantityUnitOptions={quantityUnitOptions}
          quantityUnitDisabled
          withFrequency
          frequencyUnitOptions={frequencyUnitOptions}
          value={form.values.precificacao}
          onChange={(pricingData) => {
            form.setFieldValue('precificacao', pricingData);
          }}
        />
      </Paper>
      <Space h="xs" />
      <Paper shadow="xs" p="md" withBorder>
        <Group justify="space-between">
          <PageSection
            size="lg"
            color={Feature.Home.Proposal.color}
            label="Cotações"
            text="Cadastre e revise as cotações deste item do plano."
          />
          {props.modoEdicao && props.plan && props.onAddQuotation && (
            <Button
              type="button"
              size="xs"
              leftSection={<IconPlus size={16} />}
              disabled={!canAddQuotation}
              onClick={() => {
                props.onAddQuotation?.(props.plan!);
              }}
            >
              Adicionar cotação
            </Button>
          )}
        </Group>
        <Space h="xs" />

        {!buildDraftRefItem.idResiduoEquipamento && (
          <Text size="sm" c="dimmed">
            Selecione o equipamento do item do plano para cadastrar fornecedores.
          </Text>
        )}

        {!!buildDraftRefItem.idResiduoEquipamento && !visibleEquipmentQuotations.length && (
          <Text size="sm" c="dimmed">
            Nenhuma cotação disponível para este plano.
          </Text>
        )}

        {!!buildDraftRefItem.idResiduoEquipamento &&
          visibleEquipmentQuotations.map((quotation) => {
            const quotationKey = getQuotationKey(quotation);
            const quotationItem =
              quotation.equipamentos.find(
                (entry) =>
                  (entry.action || Action.Nothing) !== Action.Delete &&
                  matchesQuotationItem(entry, buildDraftRefItem)
              ) || null;

            return (
              <Paper key={quotationKey} shadow="xs" p="md" withBorder mt="sm">
                <Group justify="space-between">
                  <Group gap="xs">
                    <Text fw={500}>Cotação #{quotation.idPropostaResiduoPlanoCotacao || '-'}</Text>
                    {quotation.primario && (
                      <Badge color="green" variant="light">
                        Primária
                      </Badge>
                    )}
                  </Group>

                  <Group gap="xs">
                    {props.modoEdicao && props.plan && props.onSetPrimaryQuotation && !quotation.primario && (
                      <Tooltip label="Definir como primária" withArrow>
                        <ActionIcon
                          size="sm"
                          color="green"
                          variant="light"
                          disabled={!quotation.completo || !quotationItem}
                          onClick={() => props.onSetPrimaryQuotation?.(props.plan!, quotation)}
                        >
                          <IconCheck size={15} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                    {props.modoEdicao && props.plan && props.onDeleteQuotation && (
                      <DeleteConfirmPopover
                        label="Excluir cotação"
                        message="Esta ação removerá a cotação do item do plano."
                        zIndex={props.zIndex + 1}
                        onConfirm={() => props.onDeleteQuotation?.(props.plan!, quotation)}
                      />
                    )}
                  </Group>
                </Group>

                {props.onQuotationSubmit && (
                  <>
                    <Space h="xs" />
                    <ResidueQuotationEquipmentForm
                      ref={(instance) => {
                        quotationFormRefs.current[quotationKey] = instance;
                      }}
                      modoEdicao={props.modoEdicao}
                      requireSupplier={props.requireSupplier}
                      referenceData={props.referenceData}
                      origItem={quotationItem}
                      item={quotationItem}
                      refItem={buildDraftRefItem}
                      idPropostaResiduoPlanoCotacao={quotation.idPropostaResiduoPlanoCotacao}
                      zIndex={props.zIndex + 1}
                      hideCustomerSection={
                        buildDraftRefItem.precificacao?.tipo === ProposalTipoPrecificacaoEnum.Geral
                      }
                      submitLabel="Salvar cotação"
                      callback={async (item, refItem, action, confirmed) => {
                        if (!confirmed || !item || !refItem) {
                          return;
                        }

                        const savedRefItem = await savePlanItem('autosave', false);

                        if (!savedRefItem) {
                          return;
                        }

                        props.onQuotationSubmit?.(quotation, savedRefItem, item, action);
                        showNotification({
                          title: 'Plano/Equipamento - Cotação',
                          message: 'Cotação salva com sucesso.',
                          color: 'green',
                        });
                      }}
                    />
                  </>
                )}
              </Paper>
            );
          })}
      </Paper>
      <Space h="xl" />
      <Group justify="right">
        {props.modoEdicao ? (
          <Button type="button" leftSection={<IconDeviceFloppy size={18} />} onClick={() => handleSubmit()}>
            Salvar
          </Button>
        ) : (
          <Button type="button" onClick={props.onClose}>
            Fechar
          </Button>
        )}
      </Group>
    </Modal>
  );
}
